import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  signInAnonymously,
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithCredential,
  linkWithRedirect,
  linkWithCredential,
  getRedirectResult,
  onAuthStateChanged,
} from 'firebase/auth';
import type { User, Auth } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { isFirebaseConfigured, initFirebase } from '../firebase';
import { AuthContext, type AuthContextType } from './authContextDef';
import { STORAGE_KEYS, FIRESTORE } from '../constants';
import { isNativePlatform } from '../utils/platform';

// Re-export types and context
export { AuthContext, type AuthContextType } from './authContextDef';

const googleProvider = new GoogleAuthProvider();

/** localStorage key to flag that a redirect auth flow is in progress */
const REDIRECT_PENDING_KEY = 'talkgame_auth_redirect_pending';
/** localStorage key to store the redirect type ('link' or 'signin') */
const REDIRECT_TYPE_KEY = 'talkgame_auth_redirect_type';

/**
 * Migrate local data (progress + stickers) to a new user's Firestore document.
 * Called after signInWithRedirect to preserve the anonymous session's data.
 */
function migrateLocalDataToFirestore(uid: string, fireDb: Firestore): void {
  try {
    const progressRaw = localStorage.getItem(STORAGE_KEYS.PROGRESS);
    const stickersRaw = localStorage.getItem(STORAGE_KEYS.STICKERS);
    const progress = progressRaw ? JSON.parse(progressRaw) : null;
    const stickers = stickersRaw ? JSON.parse(stickersRaw) : null;

    // Only migrate if there's actual data to preserve
    if (!progress && !stickers) return;

    const payload: Record<string, unknown> = {
      [`${FIRESTORE.FIELDS.METADATA}.lastSynced`]: new Date().toISOString(),
      [`${FIRESTORE.FIELDS.METADATA}.isAnonymous`]: false,
    };
    if (progress) payload[FIRESTORE.FIELDS.PROGRESS] = progress;
    if (stickers) {
      // Stickers are stored as an array of IDs in localStorage,
      // but Firestore expects { earnedIds: string[] }
      payload[FIRESTORE.FIELDS.STICKERS] = Array.isArray(stickers)
        ? { earnedIds: stickers }
        : stickers;
    }

    const userDocRef = doc(fireDb, FIRESTORE.USERS_COLLECTION, uid);
    setDoc(userDocRef, payload, { merge: true }).catch((_err) => {
      // Best-effort migration — don't block auth flow
    });
  } catch (_err) {
    // JSON parse or localStorage failure — skip migration
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  // If Firebase is not configured, no loading is needed (local-only mode)
  const [isLoading, setIsLoading] = useState(isFirebaseConfigured);
  const [isRedirecting, setIsRedirecting] = useState(
    () => isFirebaseConfigured && localStorage.getItem(REDIRECT_PENDING_KEY) === 'true',
  );
  const [error, setError] = useState<string | null>(null);
  const redirectHandledRef = useRef(false);

  // Refs to hold the lazily-loaded Firebase instances
  const authRef = useRef<Auth | null>(null);
  const dbRef = useRef<Firestore | null>(null);

  // Lazily initialise Firebase and set up auth listeners
  useEffect(() => {
    if (!isFirebaseConfigured) return;

    let unsubscribe: (() => void) | null = null;

    initFirebase().then(({ auth: fireAuth, db: fireDb }) => {
      authRef.current = fireAuth;
      dbRef.current = fireDb;

      if (!fireAuth) {
        setIsLoading(false);
        return;
      }

      // Handle redirect result (runs once after returning from Google sign-in)
      if (!redirectHandledRef.current) {
        redirectHandledRef.current = true;

        const hasPendingRedirect = localStorage.getItem(REDIRECT_PENDING_KEY) === 'true';
        const redirectType = localStorage.getItem(REDIRECT_TYPE_KEY);

        localStorage.removeItem(REDIRECT_PENDING_KEY);
        localStorage.removeItem(REDIRECT_TYPE_KEY);

        if (hasPendingRedirect) {
          getRedirectResult(fireAuth)
            .then((result) => {
              if (result?.user) {
                if (redirectType === 'signin' && fireDb) {
                  migrateLocalDataToFirestore(result.user.uid, fireDb);
                }
              }
            })
            .catch((err: unknown) => {
              const firebaseError = err as { code?: string; message?: string };
              if (firebaseError.code === 'auth/credential-already-in-use') {
                setError('account-exists');
              } else {
                setError(firebaseError.message || 'Failed to complete sign in');
              }
            })
            .finally(() => {
              setIsRedirecting(false);
            });
        } else {
          setIsRedirecting(false);
        }
      }

      // Listen to Firebase auth state changes
      unsubscribe = onAuthStateChanged(fireAuth, (firebaseUser) => {
        if (firebaseUser) {
          setUser(firebaseUser);
          setIsLoading(false);
        } else {
          // No user — sign in anonymously
          signInAnonymously(fireAuth).catch((_err) => {
            setIsLoading(false);
          });
        }
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const linkWithGoogle = useCallback(async () => {
    const fireAuth = authRef.current;
    if (!fireAuth || !user) return;
    setError(null);
    try {
      if (isNativePlatform()) {
        // Native: use Capacitor Firebase Authentication plugin for native Google
        // Sign-In sheet, then bridge the credential to Firebase JS SDK.
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) throw new Error('No ID token returned from native Google Sign-In');
        const credential = GoogleAuthProvider.credential(idToken);
        await linkWithCredential(user, credential);
      } else {
        localStorage.setItem(REDIRECT_PENDING_KEY, 'true');
        localStorage.setItem(REDIRECT_TYPE_KEY, 'link');
        await linkWithRedirect(user, googleProvider);
      }
    } catch (err: unknown) {
      localStorage.removeItem(REDIRECT_PENDING_KEY);
      localStorage.removeItem(REDIRECT_TYPE_KEY);
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code === 'auth/credential-already-in-use') {
        setError('account-exists');
      } else {
        setError(firebaseError.message || 'Failed to link account');
      }
    }
  }, [user]);

  const signInWithGoogle = useCallback(async () => {
    const fireAuth = authRef.current;
    const fireDb = dbRef.current;
    if (!fireAuth) return;
    setError(null);
    try {
      if (isNativePlatform()) {
        // Native: use Capacitor Firebase Authentication plugin for native Google
        // Sign-In sheet, then bridge the credential to Firebase JS SDK.
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) throw new Error('No ID token returned from native Google Sign-In');
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await signInWithCredential(fireAuth, credential);
        if (userCredential?.user && fireDb) {
          migrateLocalDataToFirestore(userCredential.user.uid, fireDb);
        }
      } else {
        localStorage.setItem(REDIRECT_PENDING_KEY, 'true');
        localStorage.setItem(REDIRECT_TYPE_KEY, 'signin');
        await signInWithRedirect(fireAuth, googleProvider);
      }
    } catch (err: unknown) {
      localStorage.removeItem(REDIRECT_PENDING_KEY);
      localStorage.removeItem(REDIRECT_TYPE_KEY);
      const firebaseError = err as { code?: string; message?: string };
      setError(firebaseError.message || 'Failed to sign in');
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    const fireAuth = authRef.current;
    if (!fireAuth) return;
    setError(null);
    try {
      // On native, sign out from both the native plugin and Firebase JS SDK
      if (isNativePlatform()) {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.signOut();
      }
      await fireAuth.signOut();
    } catch (err: unknown) {
      const firebaseError = err as { message?: string };
      setError(firebaseError.message || 'Failed to sign out');
    }
  }, []);

  const value: AuthContextType = {
    isLoading,
    isRedirecting,
    isAuthenticated: !isFirebaseConfigured || !!user,
    isAnonymous: !user || user.isAnonymous,
    user,
    uid: user?.uid ?? null,
    linkWithGoogle,
    signInWithGoogle,
    signOut: handleSignOut,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
